import type { ComponentType } from 'react';
import {
  type AnySpec,
  type KindMap,
  type KindOf,
  readField,
  type SpecOf,
  type UnitValues
} from 'v5/core/form-schema/formKinds.types';
import { rowIdUtils } from 'v5/core/form-schema/formRows';
import type {
  EditUnit,
  EntitySchema,
  ScopeSectionId
} from 'v5/core/form-schema/formSchema.types';
import { hasListLines } from './ListLinesField';
import type { FormKit, UnitBindings, UnitTarget } from './schemaForm.types';
import { fieldPath, listEntryId } from './unitPath';

/** A field of the form as a jump finds it: by its name or by a value it holds. */
export type FieldJumpEntry = {
  /** Unique within the form. */
  key: string;
  section: { id: ScopeSectionId; title: string };
  /** The group or list the field belongs to; `null` when the unit is the field. */
  unitTitle: string | null;
  label: string;
  /** The kind's icon (`FieldKit.icon`); `null` without one. */
  Icon: ComponentType<{ className?: string }> | null;
  editable: boolean;
  /** Where the field's name leads. */
  target: UnitTarget;
  /** Its values as text, one per list row, each leading to its row. */
  values: Array<{ text: string; target: UnitTarget }>;
};

export type FieldJumpResult = {
  entry: FieldJumpEntry;
  target: UnitTarget;
  /** The value that matched, else all of them; `null` for none. */
  valueText: string | null;
};

type FieldJumpGroup = {
  section: FieldJumpEntry['section'];
  results: FieldJumpResult[];
};

/** Every field of `schema`, in the form's order, with the values `bindings` hold. */
export function buildFieldJumpEntries<M extends KindMap<M>>(
  schema: EntitySchema<AnySpec<M>>,
  bindings: UnitBindings<M>,
  kit: FormKit<M>
): FieldJumpEntry[] {
  return schema.sections.flatMap(section =>
    section.units.flatMap((unit): FieldJumpEntry[] => {
      const base = {
        section: { id: section.id, title: section.title },
        editable: unit.editability.type === 'editable'
      };
      const binding = bindings[unit.id];
      if (unit.type === 'fields') {
        const values = binding?.type === 'fields' ? binding.values : null;
        return unit.fields.map(field => {
          const target = {
            unitId: unit.id,
            fieldPath: fieldPath('', field.key)
          };
          const text = values === null ? null : valueText(kit, field, values);
          return {
            ...base,
            ...naming(unit, unit.fields, field),
            Icon: kindIcon(kit, field),
            key: `${unit.id}#${field.key}`,
            target,
            values: text === null ? [] : [{ text, target }]
          };
        });
      }
      const items = binding?.type === 'list' ? binding.items : [];
      // As `ListUnitView` edits it: one form of lines, or entry by entry.
      const lines = hasListLines(kit, unit.item.fields);
      return unit.item.fields.map(field => ({
        ...base,
        ...naming(unit, unit.item.fields, field),
        Icon: kindIcon(kit, field),
        key: `${unit.id}#${field.key}`,
        target: { unitId: unit.id, fieldPath: null },
        values: items.flatMap((item, index) => {
          const text = valueText(kit, field, item.values);
          if (text === null) return [];
          const target = lines
            ? {
                unitId: unit.id,
                fieldPath: fieldPath(`items.${index}.values`, field.key)
              }
            : {
                unitId: listEntryId(unit.id, rowIdUtils.key(item.rowId)),
                fieldPath: fieldPath('items.0.values', field.key)
              };
          return [{ text, target }];
        })
      }));
    })
  );
}

/**
 * The entries matching every word of `query` in their name (the section's
 * title may narrow it down), else in one of their values; grouped by
 * section, the best match first. An empty query lists every field.
 */
export function searchFieldJump(
  entries: ReadonlyArray<FieldJumpEntry>,
  query: string
): FieldJumpGroup[] {
  const words = normalize(query).split(' ').filter(Boolean);
  const scored = entries.flatMap(entry => {
    const match = matchEntry(entry, words);
    return match === null ? [] : [match];
  });
  const groups = new Map<
    ScopeSectionId,
    { group: FieldJumpGroup; best: number; order: number }
  >();
  scored.forEach(({ result, score }, order) => {
    const { section } = result.entry;
    const existing = groups.get(section.id);
    if (existing) {
      existing.group.results.push(result);
      existing.best = Math.max(existing.best, score);
    } else {
      groups.set(section.id, {
        group: { section, results: [result] },
        best: score,
        order
      });
    }
  });
  const scoreOf = new Map(scored.map(({ result, score }) => [result, score]));
  return [...groups.values()]
    .sort((a, b) => b.best - a.best || a.order - b.order)
    .map(({ group }) => ({
      ...group,
      // `sort` is stable: equal scores keep the form's order.
      results: group.results.sort(
        (a, b) => (scoreOf.get(b) ?? 0) - (scoreOf.get(a) ?? 0)
      )
    }));
}

function matchEntry(
  entry: FieldJumpEntry,
  words: ReadonlyArray<string>
): { result: FieldJumpResult; score: number } | null {
  const allValues =
    entry.values.length === 0
      ? null
      : entry.values.map(value => value.text).join(', ');
  if (words.length === 0) {
    return {
      result: { entry, target: entry.target, valueText: allValues },
      score: 0
    };
  }
  const name = normalize(`${entry.unitTitle ?? ''} ${entry.label}`);
  const withSection = `${name} ${normalize(entry.section.title)}`;
  if (
    words.some(word => name.includes(word)) &&
    words.every(word => withSection.includes(word))
  ) {
    return {
      result: { entry, target: entry.target, valueText: allValues },
      score: nameScore(normalize(entry.label), name, words)
    };
  }
  const value = entry.values.find(candidate => {
    const text = normalize(candidate.text);
    return words.every(word => text.includes(word));
  });
  return value === undefined
    ? null
    : {
        result: { entry, target: value.target, valueText: value.text },
        score: 0.5
      };
}

/** How the result names the field: a unit of one field goes by the unit's title. */
function naming<M extends KindMap<M>>(
  unit: EditUnit<AnySpec<M>>,
  fields: ReadonlyArray<AnySpec<M>>,
  field: AnySpec<M>
): { unitTitle: string | null; label: string } {
  return fields.length === 1
    ? { unitTitle: null, label: unit.title }
    : { unitTitle: unit.title, label: field.label };
}

function valueText<M extends KindMap<M>, K extends KindOf<M>>(
  kit: FormKit<M>,
  spec: SpecOf<M, K>,
  values: UnitValues<M>
): string | null {
  const text = kit[spec.kind].text;
  if (text === undefined || !(spec.key in values)) return null;
  return text(readField<M, K>(values, spec), spec);
}

function kindIcon<M extends KindMap<M>, K extends KindOf<M>>(
  kit: FormKit<M>,
  spec: SpecOf<M, K>
): ComponentType<{ className?: string }> | null {
  return kit[spec.kind].icon?.(spec) ?? null;
}

/**
 * Lower case, accents dropped (`Łódź` → `lodz`), thousands unseparated
 * (`1,250` → `1250`, as amounts are shown), single spaces.
 */
function normalize(text: string): string {
  return text
    .replace(/(\d),(?=\d{3}\b)/g, '$1')
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .replace(/ł/gi, 'l')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

/** A label starting with the query, then every word starting a word, then any match. */
function nameScore(
  label: string,
  name: string,
  words: ReadonlyArray<string>
): number {
  if (label.startsWith(words.join(' '))) return 3;
  if (words.every(word => startsAWord(name, word))) return 2;
  return 1;
}

function startsAWord(text: string, word: string): boolean {
  return text.startsWith(word) || text.includes(` ${word}`);
}
